// src/components/Seller/SellerDashboard.js
import React, { useEffect, useState } from "react";
import "../../styles/SellerDashboard.css";
import config from "../../config";

export default function SellerDashboard({ sellerId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState({
    totalSales: 0,
    totalOrders: 0,
    deliveredOrders: 0,
    pendingOrders: 0,
    cancelledOrders: 0,
    totalEarnings: 0,
    pendingPayout: 0,
    recentOrders: [],
    recentPayouts: [],
  });

  useEffect(() => {
    if (!sellerId) return;
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  const loadDashboard = async () => {
    setLoading(true);
    setError("");

    try {
      // ⚠️ Adjust these endpoints if your paths are different
      const [ordersRes, payoutsRes] = await Promise.all([
        fetch(`${config.baseUrl}/api/seller/orders/${sellerId}`),
        fetch(`${config.baseUrl}/api/seller/payouts/${sellerId}`),
      ]);

      let orders = [];
      if (ordersRes.ok) {
        orders = await ordersRes.json();
      } else {
        console.warn("Failed to fetch seller orders");
      }

      let payouts = [];
      if (payoutsRes.ok) {
        payouts = await payoutsRes.json();
      } else {
        console.warn("Failed to fetch seller payouts");
      }

      // ---------- COMPUTE ORDER METRICS ----------
      // Adjust field names based on your Order DTO
      const totalSales = orders.reduce((sum, o) => {
        const amount =
          o.totalAmount ??
          o.totalPrice ??
          o.grandTotal ??
          0; // pick whatever field you actually use
        return sum + (amount || 0);
      }, 0);

      const totalOrders = orders.length;

      const deliveredOrders = orders.filter(
        (o) => (o.status || "").toUpperCase() === "DELIVERED"
      ).length;

      const cancelledOrders = orders.filter((o) =>
        ["CANCELLED", "CANCELED"].includes((o.status || "").toUpperCase())
      ).length;

      const pendingOrders = orders.filter((o) => {
        const s = (o.status || "").toUpperCase();
        return ["PENDING", "PLACED", "CONFIRMED", "SHIPPED", "OUT_FOR_DELIVERY"].includes(
          s
        );
      }).length;

      // Sort orders by createdDate / orderDate desc for "recent"
      const sortedOrders = [...orders].sort((a, b) => {
        const da = new Date(a.createdDate || a.orderDate || 0).getTime();
        const db = new Date(b.createdDate || b.orderDate || 0).getTime();
        return db - da;
      });
      const recentOrders = sortedOrders.slice(0, 5);

      // ---------- COMPUTE PAYOUT METRICS ----------
      // Adjust field names based on your SellerPayoutDTO
      const totalEarnings = payouts.reduce((sum, p) => {
        const earning = p.sellerEarning ?? p.amount ?? 0;
        return sum + (earning || 0);
      }, 0);

      const pendingPayout = payouts
        .filter((p) => (p.status || "").toUpperCase() !== "PAID")
        .reduce((sum, p) => {
          const earning = p.sellerEarning ?? p.amount ?? 0;
          return sum + (earning || 0);
        }, 0);

      // Sort payouts by paidAt / periodEnd desc
      const sortedPayouts = [...payouts].sort((a, b) => {
        const da = new Date(a.paidAt || a.periodEnd || 0).getTime();
        const db = new Date(b.paidAt || b.periodEnd || 0).getTime();
        return db - da;
      });
      const recentPayouts = sortedPayouts.slice(0, 5);

      setSummary({
        totalSales,
        totalOrders,
        deliveredOrders,
        pendingOrders,
        cancelledOrders,
        totalEarnings,
        pendingPayout,
        recentOrders,
        recentPayouts,
      });
    } catch (err) {
      console.error("Error loading seller dashboard:", err);
      setError("Failed to load dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    if (num == null || isNaN(num)) return "₹0.00";
    return `₹${Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="seller-dashboard-container">
      <div className="seller-dashboard-header">
        <div>
          <h2>📊 Seller Dashboard</h2>
          <p>Quick overview of your sales and payouts.</p>
        </div>
        <button
          className="seller-dashboard-refresh"
          onClick={loadDashboard}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "⟳ Refresh"}
        </button>
      </div>

      {error && <div className="seller-dashboard-error">{error}</div>}

      {/* TOP SUMMARY CARDS */}
      <div className="seller-dashboard-grid">
        <div className="sd-card primary">
          <p className="sd-label">Total Sales</p>
          <h2 className="sd-amount">{formatCurrency(summary.totalSales)}</h2>
          <p className="sd-sub">All time</p>
        </div>

        <div className="sd-card">
          <p className="sd-label">Total Earnings</p>
          <h2 className="sd-amount">{formatCurrency(summary.totalEarnings)}</h2>
          <p className="sd-sub">After AuraKart commission</p>
        </div>

        <div className="sd-card warning">
          <p className="sd-label">Pending Payout</p>
          <h2 className="sd-amount">{formatCurrency(summary.pendingPayout)}</h2>
          <p className="sd-sub">Will be paid in next cycle</p>
        </div>

        <div className="sd-card neutral">
          <p className="sd-label">Total Orders</p>
          <h2 className="sd-amount">{summary.totalOrders}</h2>
          <p className="sd-sub">
            ✅ {summary.deliveredOrders} Delivered · ⏳ {summary.pendingOrders}{" "}
            Pending · ❌ {summary.cancelledOrders} Cancelled
          </p>
        </div>
      </div>

      {/* LOWER SECTION: ORDERS + PAYOUTS */}
      <div className="seller-dashboard-lower">
        {/* RECENT ORDERS */}
        <div className="sd-panel">
          <div className="sd-panel-header">
            <h3>🧾 Recent Orders</h3>
          </div>
          {summary.recentOrders.length === 0 ? (
            <p className="sd-empty">No orders yet.</p>
          ) : (
            <div className="sd-table-wrapper">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentOrders.map((o) => (
                    <tr key={o.orderId || o.id}>
                      <td>{o.orderId || o.id}</td>
                      <td>{formatDateTime(o.createdDate || o.orderDate)}</td>
                      <td>
                        <span className={`sd-status sd-status-${(o.status || "").toLowerCase()}`}>
                          {o.status || "-"}
                        </span>
                      </td>
                      <td>{formatCurrency(
                        o.totalAmount ??
                          o.totalPrice ??
                          o.grandTotal ??
                          0
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* RECENT PAYOUTS */}
        <div className="sd-panel">
          <div className="sd-panel-header">
            <h3>💰 Recent Payouts</h3>
          </div>
          {summary.recentPayouts.length === 0 ? (
            <p className="sd-empty">No payouts generated yet.</p>
          ) : (
            <div className="sd-table-wrapper">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Payout ID</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentPayouts.map((p) => (
                    <tr key={p.id}>
                      <td>{p.id}</td>
                      <td>
                        {formatDateTime(p.periodStart)} –{" "}
                        {formatDateTime(p.periodEnd)}
                      </td>
                      <td>
                        <span
                          className={`sd-status sd-status-${(p.status || "").toLowerCase()}`}
                        >
                          {p.status || "-"}
                        </span>
                      </td>
                      <td>{formatCurrency(p.sellerEarning ?? p.amount ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
